/**
 * The five steps of the routine the README describes, and where a run is in them.
 *
 * Pure: the caller reads the state, this decides what it means. Every status is
 * derived from something the app actually observed — files in hand, a profile
 * chosen, cells still in the review lane, an export receipt — because a step
 * that ticks on a guess tells someone their hand-back is done when it is not.
 *
 * The `not-needed` status exists for the same reason. Plenty of runs have
 * nothing awaiting a decision, and leaving "Decide" pending forever on a clean
 * run would train people to ignore the flow. Saying "nothing to decide" is a
 * finding, not a shortcut.
 */

export type StepStatus = "done" | "current" | "pending" | "not-needed";

export type StepId = "load" | "source" | "review" | "decide" | "export";

export type GuideStep = {
  id: StepId;
  label: string;
  status: StepStatus;
  /** One line of what to do now, or what was established. Never a bare status word. */
  detail: string;
  /** Where the step happens; null when it is not reachable yet. */
  href: string | null;
};

export type GuideState = {
  /** Both export files are in hand. */
  filesLoaded: boolean;
  /**
   * A source profile is settled for this pair — detected or picked. False while
   * the run is held for an unrecognized source, which is the state that most
   * needs naming.
   */
  profileChosen: boolean;
  /** An analysis has been produced and is on screen. */
  analysisPresent: boolean;
  /**
   * Decision progress, or null when there is no review to decide anything about.
   * `awaiting` counts cells the policy puts in the review lane; `decided` counts
   * those of them that now carry a decision.
   */
  decisions: { awaiting: number; decided: number } | null;
  /** An artifact download started for this run. */
  exported: boolean;
};

const LABELS: Record<StepId, string> = {
  load: "Load pair",
  source: "Confirm source",
  review: "Review",
  decide: "Decide",
  export: "Export & archive"
};

/**
 * The five steps, in order, each with a status and a line of guidance.
 *
 * Exactly one step is `current`: the first that is neither done nor skipped.
 * Everything after it is `pending`, so the flow never shows two places to be at
 * once.
 */
export function buildSteps(state: GuideState): GuideStep[] {
  const decideStatus = decideOutcome(state);

  const settled: Array<{ id: StepId; done: boolean; skipped: boolean; detail: string; href: string | null }> = [
    {
      id: "load",
      done: state.filesLoaded,
      skipped: false,
      detail: state.filesLoaded ? "Both exports are loaded." : "Pick the reference and candidate exports, or load the sample pair.",
      href: "/"
    },
    {
      id: "source",
      done: state.filesLoaded && state.profileChosen,
      skipped: false,
      detail: sourceDetail(state),
      href: "/"
    },
    {
      id: "review",
      done: state.analysisPresent,
      skipped: false,
      detail: state.analysisPresent ? "Results are ready to read." : "Run the analysis to see what changed.",
      href: state.analysisPresent ? "/results?tab=overview" : null
    },
    {
      id: "decide",
      done: decideStatus === "done",
      skipped: decideStatus === "not-needed",
      detail: decideDetail(state, decideStatus),
      href: state.analysisPresent ? "/results?tab=recovery" : null
    },
    {
      id: "export",
      done: state.exported,
      skipped: false,
      detail: state.exported
        ? "An artifact has been downloaded from this run."
        : "Download the recovered export and the audit bundle.",
      href: state.analysisPresent ? "/results?tab=recovery" : null
    }
  ];

  let currentTaken = false;
  return settled.map((step) => {
    let status: StepStatus;
    if (step.skipped) {
      status = "not-needed";
    } else if (step.done) {
      status = "done";
    } else if (!currentTaken) {
      status = "current";
      currentTaken = true;
    } else {
      status = "pending";
    }
    return { id: step.id, label: LABELS[step.id], status, detail: step.detail, href: step.href };
  });
}

function sourceDetail(state: GuideState): string {
  if (!state.filesLoaded) return "The profile is detected from the files once they are loaded.";
  if (state.profileChosen) return "A source profile governs this run.";
  return "No profile matched. Pick one, or measure a draft from these files.";
}

function decideOutcome(state: GuideState): StepStatus {
  if (state.decisions === null) return "pending";
  if (state.decisions.awaiting === 0) return "not-needed";
  return state.decisions.decided >= state.decisions.awaiting ? "done" : "current";
}

function decideDetail(state: GuideState, status: StepStatus): string {
  if (state.decisions === null) return "Fields needing a call appear here after the analysis.";
  if (status === "not-needed") return "Nothing in this run is awaiting a decision.";
  const { awaiting, decided } = state.decisions;
  return decided >= awaiting
    ? `Every field awaiting a decision has one (${awaiting}).`
    : `${decided} of ${awaiting} decided. Each needs a reason, which the audit records.`;
}

/**
 * Where the flow is, for the one-line summary: the 1-based position of the
 * current step, or null when everything is settled.
 *
 * Deliberately the current step's POSITION rather than a count of settled
 * steps. The two are not the same number, because steps settle out of order —
 * exporting before deciding is ordinary — and a count reported as a position
 * produced "Step 5 of 5: Decide", a summary that contradicted itself.
 */
export function currentPosition(steps: GuideStep[]): number | null {
  const index = steps.findIndex((step) => step.status === "current");
  return index === -1 ? null : index + 1;
}
